/* Copyright (c) 2020-2025, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { SwRuntimeStoragePrefixes, SwCacheManifest } from '../sw.types';

export const SwPrefix = SwRuntimeStoragePrefixes.Gfn;
const ONE_DAY_IN_MINUTES = 24 * 60,
    ONE_DAY_IN_SECONDS = ONE_DAY_IN_MINUTES * 60;

export const GfnPrecacheVersion = 'v1';

export enum GfnSwCacheNames {
    LCARS = 'lcars',
    LCARSStatic = 'lcarsStatic',
    LCARSUserAccount = 'lcarsUserAccount',
    LCARSClientStrings = 'lcarsClientStrings',
    ServiceUrls = 'service',
    ServerInfo = 'server',
    RConfig = 'rconfig',
    GxTarget = 'gxtarget',
    Subscriptions = 'subscriptions',
    Telemetry = 'telemetry',
    Offline = 'offline',
    GfcFeedbacks = 'gfcFeedbacks',
    PNSNotifications = 'pnsNotifications',
    LoginWallData = 'loginwalldata',
    LoginWallStrings = 'loginwallstrings',
    OverallGfnSupportedLanguages = 'overallGfnSupportedLanguages',
    RootAndMall = 'rootAndMall'
}

export const GfnOfflineCache = `${SwPrefix}-${GfnSwCacheNames.Offline}-${GfnPrecacheVersion}`;

export const GfnOfflineAsset = 'offline.html';

export const GfnOfflineDBName = 'gfnclient';

export const GfnCacheManifest: SwCacheManifest = {
    redirectSanitizer: {
        rootAndMall: {
            name: GfnSwCacheNames.RootAndMall,
            cacheName: `${SwPrefix}-${GfnSwCacheNames.RootAndMall}-v1`,
            routePattern: new RegExp('^/(mall/)?$')
        }
    },
    staleWhileRevalidate: {
        lcars: {
            name: GfnSwCacheNames.LCARS,
            cacheName: `${SwPrefix}-${GfnSwCacheNames.LCARS}-v1`,
            routePattern: /https:\/\/([a-zA-Z0-9_.-])+\/graphql\?requestType=panels/,
            expiry: {
                maxEntries: 10,
                maxAgeSeconds: 14 * ONE_DAY_IN_SECONDS,
                purgeOnQuotaError: true
            }
        },
        lcarsStatic: {
            name: GfnSwCacheNames.LCARSStatic,
            cacheName: `${SwPrefix}-${GfnSwCacheNames.LCARSStatic}-v1`,
            routePattern: /https:\/\/([a-zA-Z0-9_.-])+\/graphql\?requestType=staticAppData/,
            expiry: {
                maxEntries: 5,
                maxAgeSeconds: 14 * ONE_DAY_IN_SECONDS,
                purgeOnQuotaError: true
            }
        },
        lcarsUserAccount: {
            name: GfnSwCacheNames.LCARSUserAccount,
            cacheName: `${SwPrefix}-${GfnSwCacheNames.LCARSUserAccount}-v1`,
            routePattern: /https:\/\/([a-zA-Z0-9_.-])+\/graphql\?requestType=userAccount/,
            expiry: {
                maxEntries: 2,
                maxAgeSeconds: 14 * ONE_DAY_IN_SECONDS,
                purgeOnQuotaError: true
            }
        },
        lcarsClientStrings: {
            name: GfnSwCacheNames.LCARSClientStrings,
            cacheName: `${SwPrefix}-${GfnSwCacheNames.LCARSClientStrings}-v1`,
            routePattern: /https:\/\/([a-zA-Z0-9_.-])+\/graphql\?requestType=clientStrings/,
            expiry: {
                maxEntries: 2,
                maxAgeSeconds: 14 * ONE_DAY_IN_SECONDS,
                purgeOnQuotaError: true
            }
        },
        serviceUrls: {
            name: GfnSwCacheNames.ServiceUrls,
            cacheName: `${SwPrefix}-${GfnSwCacheNames.ServiceUrls}-v1`,
            routePattern: /https:\/\/([a-zA-Z0-9_.-])+(\/v1\/serviceUrls)/,
            expiry: {
                maxEntries: 10,
                maxAgeSeconds: 14 * ONE_DAY_IN_SECONDS,
                purgeOnQuotaError: true
            }
        },
        server: {
            name: GfnSwCacheNames.ServerInfo,
            cacheName: `${SwPrefix}-${GfnSwCacheNames.ServerInfo}-v1`,
            routePattern: /https:\/\/([a-zA-Z0-9_.-])+(\/v2\/serverInfo)/,
            expiry: {
                maxEntries: 10,
                maxAgeSeconds: 14 * ONE_DAY_IN_SECONDS,
                purgeOnQuotaError: true
            }
        },
        subscriptions: {
            name: GfnSwCacheNames.Subscriptions,
            cacheName: `${SwPrefix}-${GfnSwCacheNames.Subscriptions}-v1`,
            routePattern: /https:\/\/([a-zA-Z0-9_.-])+(\/v4\/subscriptions)/,
            expiry: {
                maxEntries: 20,
                maxAgeSeconds: 7 * ONE_DAY_IN_SECONDS,
                purgeOnQuotaError: true
            },
            flushCacheOnResponseCodes: [404]
        },
        loginwall: {
            name: GfnSwCacheNames.LoginWallData,
            cacheName: `${SwPrefix}-${GfnSwCacheNames.LoginWallData}-v1`,
            routePattern: /https:\/\/([a-zA-Z0-9_.-])+\/graphql\?requestType=loginWallData/,
            expiry: {
                maxEntries: 2,
                maxAgeSeconds: 7 * ONE_DAY_IN_SECONDS,
                purgeOnQuotaError: true
            }
        },
        loginwallstrings: {
            name: GfnSwCacheNames.LoginWallStrings,
            cacheName: `${SwPrefix}-${GfnSwCacheNames.LoginWallStrings}-v1`,
            routePattern: /https:\/\/([a-zA-Z0-9_.-])+\/graphql\?requestType=loginWallStrings/,
            expiry: {
                maxEntries: 2,
                maxAgeSeconds: 7 * ONE_DAY_IN_SECONDS,
                purgeOnQuotaError: true
            }
        },
        overallGfnSupportedLanguages: {
            name: GfnSwCacheNames.OverallGfnSupportedLanguages,
            cacheName: `${SwPrefix}-${GfnSwCacheNames.OverallGfnSupportedLanguages}-v1`,
            routePattern: /https:\/\/([a-zA-Z0-9_.-])+\/graphql\?requestType=overallGfnSupportedLanguages/,
            expiry: {
                maxEntries: 1,
                maxAgeSeconds: 14 * ONE_DAY_IN_SECONDS,
                purgeOnQuotaError: true
            }
        }
    },
    networkFirst: {
        /** TODO - Enable these once timeout / refresh defined.
        rconfig: {
            name: GfnSwCacheNames.RConfig,
            cacheName: `${SwPrefix}-${GfnSwCacheNames.RConfig}-v1`,
            routePattern: /https:\/\/rconfig\.nvidiagrid\.net\//,
            expiry: {
                maxEntries: 10,
                maxAgeSeconds: 14 * ONE_DAY_IN_SECONDS,
                purgeOnQuotaError: true
            }
        },
        gxtarget: {
            name: GfnSwCacheNames.GxTarget,
            cacheName: `${SwPrefix}-${GfnSwCacheNames.GxTarget}-v1`,
            routePattern: /https:\/\/api.gfe.nvidia.com\/gxtarget\//,
            expiry: {
                maxEntries: 20,
                maxAgeSeconds: 14 * ONE_DAY_IN_SECONDS,
                purgeOnQuotaError: true
            }
        }
        */
    },
    networkOnly: {
        telemetry: {
            name: GfnSwCacheNames.Telemetry,
            cacheName: `${SwPrefix}-${GfnSwCacheNames.Telemetry}-v1`,
            routePattern: /https:\/\/events\.gfe(stage)?\.nvidia\.com\/v1\.0\/events\/json\?immediateRequest=true/,
            maxRetentionTime: ONE_DAY_IN_MINUTES
        },
        gfcFeedbacks: {
            name: GfnSwCacheNames.GfcFeedbacks,
            cacheName: `${SwPrefix}-${GfnSwCacheNames.GfcFeedbacks}-v1`,
            routePattern: /https:\/\/telemetry\.gfe(stage)?\.nvidia\.com\/gfc\/v4\.0\//,
            maxRetentionTime: ONE_DAY_IN_MINUTES
        },
        pnsNotifications: {
            name: GfnSwCacheNames.PNSNotifications,
            cacheName: `${SwPrefix}-${GfnSwCacheNames.PNSNotifications}-v1`,
            routePattern: /https:\/\/(stg.pns.nvidiagrid.net)?(pns.geforcenow.com)?\/v1\/unregister/,
            maxRetentionTime: ONE_DAY_IN_MINUTES
        }
    }
};

export interface FirebaseConfig {
    apiKey: string;
    projectId: string;
    messagingSenderId: string;
    appId: string;
}
